import { URLS } from "../env/urls";
import { sendServerRequest } from "../server/request";

/**
 * WebSocket auto-reconectável e estável:
 *  - Autentica via JWT (endpoint HTTP autenticado por sessão) a cada (re)conexão.
 *  - Reconecta sozinho com backoff exponencial (até 15s) quando cai.
 *  - Heartbeat (ping 30s) + watchdog: se ficar sem nenhuma mensagem por >70s,
 *    considera a conexão zumbi, fecha e reconecta.
 *  - close() é definitivo (não reconecta); reconnectNow() força reconexão já.
 *
 * A mesma instância sobrevive às reconexões: os handlers registrados continuam
 * válidos (são re-vinculados ao novo socket interno a cada conexão).
 */
export class ServerWebSocket{
     url:string;
     ws:WebSocket;
     private onMessage?:(data:any)=>void;
     private onOpenCb?:Function;
     private onCloseCb?:(e:any)=>void;
     private onErrorCb?:(e:any)=>void;
     private pingId?:any;
     private watchdogId?:any;
     private reconnectId?:any;
     private manualClose=false;
     private reconnectTries=0;
     private lastActivity=Date.now();

    constructor(url:string){
       this.url=url;
       this.ws=this.connect();
    }

    private connect():WebSocket{
        const ws=new WebSocket(this.url);
        this.ws=ws;
        ws.onopen=()=>{
            this.reconnectTries=0;
            this.lastActivity=Date.now();
            this.auth();
            this.startPing();
            this.startWatchdog();
            this.onOpenCb&&this.onOpenCb();
        };
        ws.onmessage=(ev:any)=>{
            this.lastActivity=Date.now();
            let sdata:any;
            try{ sdata=JSON.parse(ev.data); }catch(e){ return; }
            this.onMessage&&this.onMessage(sdata);
        };
        ws.onclose=(e:any)=>{
            this.clearTimers();
            this.onCloseCb&&this.onCloseCb(e);
            if(!this.manualClose) this.scheduleReconnect();
        };
        ws.onerror=(e:any)=>{
            this.onErrorCb&&this.onErrorCb(e);
            // onerror normalmente é seguido de onclose, onde o reconnect é agendado.
        };
        return ws;
    }

    private scheduleReconnect(){
        if(this.manualClose||this.reconnectId) return;
        const delay=Math.min(1000*Math.pow(2,this.reconnectTries),15000);
        this.reconnectTries++;
        this.reconnectId=setTimeout(()=>{
            this.reconnectId=undefined;
            if(!this.manualClose) this.connect();
        },delay);
    }

    private startPing(){
        this.clearPing();
        this.pingId=setInterval(()=>{
            if(this.ws.readyState===WebSocket.OPEN){
                try{ this.ws.send(JSON.stringify({action:"ping"})); }catch(e){}
            }
        },30000);
    }
    private clearPing(){ if(this.pingId){ clearInterval(this.pingId); this.pingId=undefined; } }

    private startWatchdog(){
        this.clearWatchdog();
        this.watchdogId=setInterval(()=>{
            // Sem nenhuma resposta (nem pong) há mais de 70s → conexão morta, reconecta.
            if(Date.now()-this.lastActivity>70000){
                try{ this.ws.close(); }catch(e){}
            }
        },15000);
    }
    private clearWatchdog(){ if(this.watchdogId){ clearInterval(this.watchdogId); this.watchdogId=undefined; } }
    private clearTimers(){ this.clearPing(); this.clearWatchdog(); }

    getWS(){
        return this.ws;
    }
    setOnopen(onopen:any){
        this.onOpenCb=onopen;
        if(this.ws.readyState===WebSocket.OPEN) onopen&&onopen();
    }
    setOnclose(onclose:any){
        this.onCloseCb=onclose;
    }
    setOnerror(onerror:any){
        this.onErrorCb=onerror;
    }
    setOnmessage(onmessage:(data:any)=>void){
        this.onMessage=onmessage;
    }
    /** Mantido por compatibilidade — o ping já roda automático ao conectar. */
    sendPing(){}

    send(data:string|any){
        try{ this.ws.send(typeof(data)=='string'?data:JSON.stringify(data)); }catch(e){}
    }
    close(reason=1000){
        this.manualClose=true;
        this.clearTimers();
        if(this.reconnectId){ clearTimeout(this.reconnectId); this.reconnectId=undefined; }
        try{ this.ws.close(reason); }catch(e){}
    }
    /** Força uma reconexão imediata (ex.: botão "Reconectar"). */
    reconnectNow(){
        this.manualClose=false;
        if(this.reconnectId){ clearTimeout(this.reconnectId); this.reconnectId=undefined; }
        this.clearTimers();
        this.reconnectTries=0;
        try{
           if(this.ws.readyState===WebSocket.OPEN||this.ws.readyState===WebSocket.CONNECTING)
              this.ws.close();
        }catch(e){}
        this.connect();
    }
    async auth(){
       try{
         const result=await sendServerRequest('POST',URLS.ws.getJwt);
         const data=result.data;
         if(!data.hasError){
             let token=data.data.token;
             this.ws.send(JSON.stringify({action:'auth_ws',token:token}));
         }
       }catch(e){
           // Se a emissão do JWT falhar, o watchdog/onclose cuidam da reconexão.
       }
    }
}
